import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Pill } from '../ui';
import { useCleanerAttendance } from '../cleaners/useCleanerAttendance';
import { CleanerStoreRow } from '../cleaners/cleanerAttendanceTypes';
import {
  cleanerName,
  eventLabel,
  formatAbsolute,
  formatAgo,
  formatTime,
  statusLabel,
  statusTone,
  storeAttendanceStatus,
} from '../cleaners/cleanerAttendanceUtils';

type Props = {
  locationId: number | null;
};

function subLine(row: CleanerStoreRow): string {
  if (row.cleanerOnSiteNow) {
    return `${cleanerName(row.cleanerOnSiteNow.cleaner)} on site since ${formatTime(
      row.cleanerOnSiteNow.clockIn
    )}`;
  }
  if (row.lastAttended) {
    return `Last: ${cleanerName(row.lastAttended.cleaner)}, ${formatAgo(
      row.lastAttended.clockOut
    )}`;
  }
  return 'No visits recorded';
}

function buildTooltip(row: CleanerStoreRow): string {
  const recent = row.events.slice(0, 3).map((e) => {
    const site = e.offSite ? ' (off-site)' : '';
    return `${formatAbsolute(e.occurredAt)} · ${eventLabel(e.event)} · ${cleanerName(
      e.cleaner
    )}${site}`;
  });
  return recent.length
    ? ['Recent:', ...recent, '', 'Click for cleaner history'].join('\n')
    : 'No clock-ins recorded yet. Click for cleaner history';
}

const StatusTimelineCleanerCard: React.FC<Props> = ({ locationId }) => {
  const navigate = useNavigate();
  const { data, error } = useCleanerAttendance();

  if (locationId == null) return null;

  if (!data) {
    if (!error) return null;
    return (
      <div className="sq-context-card" title={error}>
        <div className="sq-context-card-top">
          <span className="sq-context-card-name">Cleaner</span>
          <Pill>Unavailable</Pill>
        </div>
      </div>
    );
  }

  const row = data.stores.find((s) => s.locationId === locationId);
  if (!row) return null;

  const status = storeAttendanceStatus(row);

  return (
    <button
      type="button"
      className="sq-context-card is-link"
      title={buildTooltip(row)}
      onClick={() => navigate(`/maintenance/cleaners?locationId=${locationId}`)}
    >
      <span className="sq-context-card-top">
        <span className="sq-context-card-name">Cleaner</span>
        <Pill tone={statusTone(status)}>{statusLabel(status)}</Pill>
      </span>
      <span className="sq-muted">{subLine(row)}</span>
    </button>
  );
};

export default StatusTimelineCleanerCard;
